import { useMemo } from 'react'
import cn from 'classnames/bind'

import { useCurrencySign } from 'app/common/components-next/currency'
import { Table, Td, Th, Tr } from 'app/common/ui-components/table'
import Sticky from 'app/common/templates-next/sticky'
import { renderCellGrayZeros } from 'app/common/templates-next/table-template'

import {
  columnKeysSimpleHeaders,
  createDataItemSimple,
  createDataProps,
  createHeaderCells,
  createHeaderCellsProps,
} from './table-options'

import { PaymentMethodsDto, ProductDataDto, ProductDataTotalDto } from './types'
import styles from './product-types-simple.module.scss'

const cx = cn.bind(styles)

type Props = {
  productTypes: ProductDataDto
  total: ProductDataTotalDto
  paymentMethods: PaymentMethodsDto
}

export const ProductTypesSimple = ({ productTypes, total, paymentMethods }: Props) => {
  const currency = useCurrencySign()

  const headerCells = useMemo(() => {
    return createHeaderCells(currency)
  }, [currency])

  const headerProps = useMemo(() => {
    return createHeaderCellsProps()
  }, [])

  const cellProps = useMemo(() => {
    return createDataProps()
  }, [])

  const rows = useMemo(() => {
    return productTypes.reduce(
      (acc, { title, items }) => {
        return [
          ...acc,
          createDataItemSimple({ title, isOnlyTitle: true }),
          ...items.map(({ title, total: { count, sum } }) =>
            createDataItemSimple({ title, count, sum }),
          ),
        ]
      },
      [] as Array<ReturnType<typeof createDataItemSimple>>,
    )
  }, [productTypes])

  const totalRows = useMemo(() => {
    return [
      createDataItemSimple({
        title: 'Внутренний счет',
        count: paymentMethods.internal.total.count,
        sum: paymentMethods.internal.total.sum,
      }),
      createDataItemSimple({
        title: 'СБП',
        count: paymentMethods.sbp.total.count,
        sum: paymentMethods.sbp.total.sum,
      }),
      createDataItemSimple({
        title: 'Банковская карта',
        count: paymentMethods.bank_card.total.count,
        sum: paymentMethods.bank_card.total.sum,
      }),
      createDataItemSimple({
        title: 'Наличные',
        count: paymentMethods.cash.total.count,
        sum: paymentMethods.cash.total.sum,
      }),
      createDataItemSimple({ title: 'ИТОГО', count: total.total.count, sum: total.total.sum }),
    ]
  }, [total, paymentMethods])

  return (
    <Table wide borderBottom>
      <Sticky>
        {(ref, stuck, shadow) => (
          <thead ref={ref} className={cx(styles.stickyFix)}>
            {columnKeysSimpleHeaders.map((keys, index) => (
              <Tr header key={index} headerHasBorderBottom={false}>
                {keys.map((key, idx, arr) => (
                  <Th
                    scope="col"
                    key={key}
                    {...headerProps[key]}
                    // borderShadowBottom={shadow && headerProps[key].colSpan !== 2}
                    borderTopCompensate={stuck}
                    bgFilled
                    borderBottom
                    borderRight={arr.length - 1 !== idx}
                  >
                    {headerCells[key]}
                  </Th>
                ))}
              </Tr>
            ))}
          </thead>
        )}
      </Sticky>
      <tbody>
        {rows.map(({ title, totalCount, totalSum, isOnlyTitle }, index) =>
          isOnlyTitle ? (
            <Tr key={index} interlineBorder>
              <Td {...cellProps.isOnlyTitle}>{title}</Td>
            </Tr>
          ) : (
            <Tr key={index} interlineBorder>
              <Td {...cellProps.title} style={{ paddingLeft: '50px' }} borderRight>
                {title}
              </Td>
              <Td {...cellProps.totalCount}>
                {renderCellGrayZeros({ columnKey: '', value: totalCount })}
              </Td>
              <Td {...cellProps.totalSum}>
                {renderCellGrayZeros({ columnKey: '', value: totalSum })}
              </Td>
            </Tr>
          ),
        )}
      </tbody>
      <Sticky stickyBottom>
        {(ref, stuck, shadow) => (
          <tfoot ref={ref} className={cx(styles.stickyFix)}>
            {totalRows.map(({ title, totalCount, totalSum }, index) => (
              <Tr key={index}>
                <Td {...cellProps.title} borderTop={!index} bold>
                  {title}
                </Td>
                <Td {...cellProps.totalCount} borderTop={!index} bold highlighted>
                  {renderCellGrayZeros({ columnKey: '', value: totalCount })}
                </Td>
                <Td {...cellProps.totalSum} borderTop={!index} bold highlighted>
                  {renderCellGrayZeros({ columnKey: '', value: totalSum })}
                </Td>
              </Tr>
            ))}
          </tfoot>
        )}
      </Sticky>
    </Table>
  )
}
